import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { PricingPlan, PricingPlanExtension } from 'types/pricing_plan';

import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

export function usePricingPlans({
  staleTime = Infinity,
  enabled,
  ...options
}: UseQueryOptions<PricingPlan[], APIError> = {}) {
  const { client } = useAPI();
  const { data: organization } = useCurrentOrganization();

  return useAuthenticatedQuery(
    ['pricing-plans', organization?.id],
    async () => {
      if (!organization?.id) {
        return Promise.reject(new Error('No organization id provided.'));
      }

      const { data } = await client.get<PricingPlan[]>(
        `/internal/orgs/${organization.id}/plans`
      );

      return data?.map((plan) => {
        // Attempt to parse the plan description as a JSON object
        // If it fails, return the plan as is
        if (!plan.description) return plan;
        try {
          const parsedExtension = JSON.parse(
            plan.description
          ) as PricingPlanExtension;
          return {
            ...plan,
            description: parsedExtension.tagline,
            extension: parsedExtension,
          };
        } catch {
          return plan;
        }
      });
    },
    {
      ...options,
      staleTime,
      enabled:
        enabled !== undefined
          ? enabled && Boolean(organization)
          : Boolean(organization),
    }
  );
}
